import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs/internal/Observable";
import { SettingsService } from "../../settings/settings.service";
import { Store } from "@ngrx/store";
import { AppState } from "../..";
import { BaseService } from "../../services/base.service";
import { map } from "rxjs/operators";
import { ProductHistoryModel } from "../models/product-history.model";
import { ProductFlowStatus } from "../models/productFlowStatus.enum";
import { HistoryState } from "../models/historyState";

export interface BoosterAccessRequestPayload {
  boosterTitle: string;
  boosterProductCodes: string[];
  boosterContractTemplateIds: string[];
  adminPartyId?: string;
  requestorHasSubscription: boolean;
  adminSubscriptionUrl: string;
  adminReviewUrl: string;
}

export interface BoosterAccessRequestResponse {
  success: boolean;
  adminHasAccess: boolean;
  emailType: "add-member-to-booster-subscription" | "booster-access-requested";
  message: string;
}

export interface PendingBoosterAccessRequest {
  boosterAccessRequestId: string;
  boosterTitle: string;
  boosterProductCode: string;
  boosterContractTemplateId: string;
  status: string;
  createdAtUtc: string;
}

@Injectable({
  providedIn: "root",
})
export class ProductService extends BaseService {
  private readonly baseUrl;
  private readonly apiVersion;

  private proccessStatus(status: number): string {
    if (Object.values(ProductFlowStatus).includes(status))
      return (ProductFlowStatus[status] ?? "Unknown").replace("_", " ");
    else return `Unknown status (${status})`;
  }

  constructor(
    private readonly client: HttpClient,
    protected settingsService: SettingsService,
    private readonly store: Store<AppState>,
  ) {
    super(client, settingsService);
    this.baseUrl = settingsService.getSettings().baseUrl;
    this.apiVersion = settingsService.getSettings().apiVersion;
  }

  public getProducts(): Observable<any> {
    return this.get(
      this.baseUrl +
        "lightstone-productmanagement-webapi/" +
        this.apiVersion +
        "/Product/Get",
      null,
    ).pipe(
      map((res) => {
        return res.body;
      }),
    );
  }

  public getFlow(id: string): Observable<any> {
    return this.get(
      this.baseUrl +
        "lightstone-productmanagement-webapi/" +
        this.apiVersion +
        "/Flows/" +
        id,
    ).pipe(
      map((res) => {
        return res.body;
      }),
    );
  }

  public getPurchaseHistory(options: HistoryState): Observable<any> {
    try {
      const queryParams = this.buildQueryParams(options);
      const url = `${this.baseUrl}lightstone-productmanagement-webapi/${this.apiVersion}/Flows/PurchaseHistory${queryParams}`;

      return this.get(url).pipe(
        map((res) => this.processHistoryResponse(res, options)),
      );
    } catch (error) {
      console.log("Error in getPurchaseHistory: ", error);
      return undefined;
    }
  }

  private buildQueryParams(options: HistoryState): string {
    const params = new URLSearchParams();

    if (options.fromDate) params.append("fromDate", options.fromDate);
    if (options.pageSize)
      params.append("pageSize", options.pageSize.toString());
    if (options.page) params.append("page", options.page.toString());
    if (
      options.shareHistoryFilter !== undefined &&
      options.shareHistoryFilter !== null
    ) {
      params.append(
        "shareHistoryFilter",
        Number(options.shareHistoryFilter).toString(),
      );
    }
    if (options.productCode) params.append("productCode", options.productCode);
    if (options.dateTo) params.append("dateTo", options.dateTo);
    if (options.description) params.append("description", options.description);
    if (options.sortField) params.append("SortField", options.sortField);
    if (
      options.sortDescending != null &&
      options.sortDescending !== undefined
    ) {
      params.append("SortDescending", options.sortDescending.toString());
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : "";
  }

  private processHistoryResponse(res: any, options: HistoryState): any {
    if (this.isLegacyResponse(res.body)) {
      return this.buildLegacyHistoryResponse(res.body, options);
    }

    return this.buildStandardHistoryResponse(res.body, options);
  }

  private isLegacyResponse(body: any): boolean {
    return (
      body.productFlowHistory === undefined || body.productFlowHistory === null
    );
  }

  private buildLegacyHistoryResponse(body: any, options: HistoryState): any {
    return {
      pageNumber: 1,
      callingTabstate: options.tabName,
      numberOfPages: 1,
      productFlowHistory: body.map((item: any) => this.mapToHistoryModel(item)),
    };
  }

  private buildStandardHistoryResponse(body: any, options: HistoryState): any {
    const filteredHistory = this.filterValidReports(body.productFlowHistory);

    return {
      pageNumber: body.pageNumber,
      callingTabstate: options.tabName,
      numberOfPages: body.numberOfPages,
      productFlowHistory: filteredHistory.map((item: any) =>
        this.mapToHistoryModel(item),
      ),
    };
  }

  private filterValidReports(productFlowHistory: any[]): any[] {
    // Filter out all reports that that have started (code: 1) and have an expiry timestamp (i.e., were never completed)   errored out at start (code: 1909)
    return productFlowHistory.filter((item: any) => {
      return !(
        (item.status === 1 && item.expiryTimestamp !== null) ||
        item.status === 1909
      );
    });
  }

  private mapToHistoryModel(body: any): ProductHistoryModel {
    return {
      created: body.created,
      expiryTimestamp: body.expiryTimestamp,
      id: body.id,
      productFlowId: body.productFlowId,
      productCode: body.productCode,
      productDescription: body.productDescription,
      productName: body.productName,
      partyId: body.partyId,
      statusCode: body.status,
      status: this.proccessStatus(body.status),
    } as ProductHistoryModel;
  }

  public getProductCatalogueById(): Observable<any> {
    const headers = {
      "x-Authenticated-TenantId": this.settingsService.getSettings().tenantId,
      "Ocp-Apim-Subscription-Key": this.settingsService.getSettings().apiKey,
    };

    const partyId = this.settingsService.getSettings().partyId;
    if (partyId) {
      headers["x-ls-party-id"] = partyId;
    }

    return this.client.get<any>(
      `${this.baseUrl || "https://test.apis.lightstone.co.za"}/lightstone-products-admin-webapi/${this.apiVersion || "v1"}/Catalogue`,
      {
        headers: headers,
      },
    );
  }

  public refreshSasUrl(documentId: string): Observable<any> {
    const headers = {
      "x-Authenticated-TenantId": this.settingsService.getSettings().tenantId,
      "Ocp-Apim-Subscription-Key": this.settingsService.getSettings().apiKey,
    };

    const partyId = this.settingsService.getSettings().partyId;
    if (partyId) {
      headers["x-ls-party-id"] = partyId;
    }

    return this.client.post<any>(
      `${this.baseUrl || "https://test.apis.lightstone.co.za"}/lightstone-products-admin-webapi/${this.apiVersion || "v1"}/ProductCatalogue/${documentId}/refresh-url`,
      {
        headers: headers,
      },
    );
  }

  public requestBoosterAccess(
    payload: BoosterAccessRequestPayload,
  ): Observable<BoosterAccessRequestResponse> {
    const headers = {
      "x-Authenticated-TenantId": this.settingsService.getSettings().tenantId,
      "Ocp-Apim-Subscription-Key": this.settingsService.getSettings().apiKey,
    };

    const partyId = this.settingsService.getSettings().partyId;
    if (partyId) {
      headers["x-ls-party-id"] = partyId;
    }

    return this.client.post<BoosterAccessRequestResponse>(
      `${this.baseUrl || "https://test.apis.lightstone.co.za"}/lightstone-products-admin-webapi/${this.apiVersion || "v1"}/BoosterAccess/Request`,
      payload,
      {
        headers,
      },
    );
  }

  public getPendingBoosterRequests(): Observable<PendingBoosterAccessRequest[]> {
    const headers = {
      "x-Authenticated-TenantId": this.settingsService.getSettings().tenantId,
      "Ocp-Apim-Subscription-Key": this.settingsService.getSettings().apiKey,
    };

    const partyId = this.settingsService.getSettings().partyId;
    if (partyId) {
      headers["x-ls-party-id"] = partyId;
    }

    return this.client.get<PendingBoosterAccessRequest[]>(
      `${this.baseUrl || "https://test.apis.lightstone.co.za"}/lightstone-products-admin-webapi/${this.apiVersion || "v1"}/BoosterAccess/PendingRequests`,
      {
        headers,
      },
    );
  }
}
